import { Node } from "prosemirror-model";

import { schema } from "@moment/prosemirror-schema";
import { DEFAULT_CELL_ID, createCellID } from "@moment/prosemirror-utils";

import { type CellInput } from "~/api/generated/graphql";
import { type Cell, type CellInfo, CellType, encodeNodeAttrs } from "~/models/cell";

import { makeComputeCell, makeTextCell } from "../helpers";
import { withoutID } from "../helpers/makeComputeCell";

export type CellMutations = {
	id: string;
	type: "text_cell" | "compute_cell";
	code: string | undefined;
	cellName?: string;
	nodeAttrs?: Record<string, string>;
};

// Returns an ordered list of cells constructed from the document.
export const documentToCellCode = (doc: Node): Cell[] => {
	const cells: Cell[] = [];

	doc.content.forEach((child: Node) => {
		switch (child.type.name) {
			case "text_cell": {
				cells.push({
					id: child.attrs["id"],
					type: CellType.ProseMirrorJSONCell,
					cellName: child.attrs["cellName"],
					code: JSON.stringify(child.toJSON()),
					nodeAttrs: withoutID(child.attrs),
				});
				break;
			}
			case "compute_cell": {
				cells.push({
					id: child.attrs["id"],
					type: CellType.JavaScriptFunctionBodyCell,
					code: child.textContent ?? child.attrs["code"],
					cellName: child.attrs["cellName"],
					nodeAttrs: withoutID(child.attrs),
				});
				break;
			}
			default: {
				const cellId =
					child.attrs["id"] === DEFAULT_CELL_ID ? createCellID() : child.attrs["id"];
				const text_cell_wrapper = {
					type: "text_cell",
					attrs: {
						id: cellId,
						isCollapsed: false,
					},
					content: [child.toJSON()],
				};
				cells.push({
					id: cellId,
					type: CellType.ProseMirrorJSONCell,
					cellName: child.attrs["cellName"],
					code: JSON.stringify(text_cell_wrapper),
					nodeAttrs: withoutID(child.attrs),
				});
				break;
			}
		}
	});

	return cells;
};

/**
 * documentToCellInputCode converts a Prosemirror document to a CellInput that GQL
 * uses to save the document.
 *
 * @param doc Prosemirror document to convert
 * @returns
 */
export const documentToCellInputCode = (doc: Node): CellInput[] => {
	const cells: CellInput[] = [];

	doc.content.forEach((child: Node) => {
		switch (child.type.name) {
			case "text_cell": {
				cells.push({
					id: child.attrs["id"],
					cellName: null, // Text cells do not have or need a cellName
					type: CellType.ProseMirrorJSONCell,
					code: JSON.stringify(child.toJSON()),
					nodeAttrs: encodeNodeAttrs(withoutID(child.attrs)),
				});
				break;
			}
			case "compute_cell": {
				cells.push({
					id: child.attrs["id"],
					type: CellType.JavaScriptFunctionBodyCell,
					cellName: child.attrs["cellName"],
					code: child.textContent,
					nodeAttrs: encodeNodeAttrs(withoutID(child.attrs)),
				});
				break;
			}
			default: {
				const cellId =
					child.attrs["id"] === DEFAULT_CELL_ID ? createCellID() : child.attrs["id"];
				const text_cell_wrapper = {
					type: "text_cell",
					attrs: {
						id: cellId,
						isCollapsed: false,
					},
					content: [child.toJSON()],
				};
				cells.push({
					id: cellId,
					type: CellType.ProseMirrorJSONCell,
					code: JSON.stringify(text_cell_wrapper),
					cellName: child.attrs["cellName"],
					nodeAttrs: encodeNodeAttrs(withoutID(child.attrs)),
				});
				break;
			}
		}
	});

	return cells;
};

export const cellsToDocument = (cells: Cell[]): Node => {
	const cellNodes = cells.map(cellToNodes);
	const cellNodesSpread = cellNodes.flat();
	return schema.nodes.doc.createChecked({}, cellNodesSpread);
};

export const cellInfosToDocument = (cells: CellInfo[]) => {
	return cellsToDocument(cells.map((cellInfo) => cellInfo.cell));
};

const nodeFromJSONString = (cellID: string, content: string): Node[] => {
	const parsed = JSON.parse(content);
	const parsedNode = Node.fromJSON(schema, parsed);

	/* Note: There are two kinds of ProseMirrorJSON cells in the wild
	 * ones where the content string encodes an entire document. These
	 * cells were from a time when ever cell initialized it's own
	 * prosemirror editor.
	 *
	 * ```json
	 *  {
	 *      type: "doc",
	 *      content: [
	 *          {
	 *              type: "paragraph",
	 *              content: [{ type: "text", text: "xyz" }],
	 *          },
	 *      ],
	 *  };
	 * ```
	 *
	 * Alternatively cells created after we had all content in one large
	 * prosemirror editor look like this:
	 *
	 * ```json
	 *  {
	 *      type: "text_cell",
	 *      attrs: {
	 *          id: "test-a",
	 *      },
	 *      content: [
	 *          {
	 *              type: "paragraph",
	 *              content: [{ type: "text", text: "abc" }],
	 *          },
	 *      ],
	 *  };
	 *```
	 * In both cases the easy thing to do is just grab the content field
	 * and replace the id with the cell id we already have.
	 *
	 */

	const newNodeAttrs = parsedNode.attrs && { ...parsedNode.attrs, id: cellID };
	return makeTextCell({
		cellID,
		content: parsedNode.content,
		nodeAttrs: newNodeAttrs,
	});
};

export const cellToNodes = (cell: Cell): Node[] => {
	switch (cell.type) {
		case CellType.ProseMirrorJSONCell: {
			try {
				if (cell.code === "") {
					return makeTextCell({ cellID: cell.id, nodeAttrs: cell.nodeAttrs ?? {} });
				}
				return nodeFromJSONString(cell.id, cell.code);
			} catch (e) {
				console.warn("creating text cell failed", e, cell);
				return makeTextCell({ cellID: cell.id });
			}
		}
		case CellType.JavaScriptFunctionBodyCell: {
			return [
				makeComputeCell({
					cellID: cell.id,
					code: cell.code,
					cellName: cell.cellName ?? "",
					nodeAttrs: cell.nodeAttrs ?? {},
				}),
			];
		}
	}
};
